/**
 * UUID v4 한 곳.
 *
 * 🔴 **`crypto.randomUUID` 는 «보안 컨텍스트»에서만 있다**(2026-09-23 실측 · eric
 *    「지금 획득 버튼을 누르면 `crypto.randomUUID is not a function` 이라는 에러가 나는데?」).
 *    보안 컨텍스트는 https 이거나 `localhost`·`127.0.0.1` 이다 — eric 이 다른 기기에서
 *    `http://10.10.20.107:5173` 처럼 **IP 로** dev 서버에 붙으면서 그 조건이 깨졌고,
 *    `crypto.randomUUID` 가 통째로 `undefined` 가 됐다. 제어락 획득이 첫 상태변경이라
 *    거기서 터졌을 뿐, **명령 봉투(`command_id`)를 만드는 모든 요청이 같은 길**이었다.
 * 🔴 **`crypto.getRandomValues` 는 비보안 컨텍스트에도 있다.** 그래서 그것으로 v4 를 짠다.
 * 🔴 **`Math.random` 으로 떨어지지 않는다.** 이 값은 멱등키(IFC-MSG-04)라 **충돌이 곧 사고**다 —
 *    계약이 「같은 `command_id` 재수신은 재실행 없이 첫 결과 반환」이라, 두 명령이 같은 id 를
 *    받으면 뒤엣것이 **조용히 실행되지 않고** 앞엣것의 결과를 되받는다. 약한 난수로 그 확률을
 *    만들 바에는 던지는 것이 맞다.
 * ⚠ 기기 표지(`device.ts`)는 규칙이 다르다 — 사람이 읽는 라벨이고 충돌해도 세션 묶음이
 *   섞이는 정도라, 그쪽은 던지지 않고 약한 값으로라도 끝낸다. 같이 묶지 마라.
 */

/** 이 환경이 쓸 수 있는 난수 바이트. 없으면 `null` — 부르는 쪽이 어떻게 할지 정한다 */
export function randomBytes(length: number): Uint8Array | null {
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') return null
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  return bytes
}

function hex(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * UUID v4 하나.
 *
 * 우선순위: `crypto.randomUUID` → `crypto.getRandomValues` 로 직접 조립 → **던진다**.
 * @throws 난수원이 아예 없을 때. 조용히 약한 값을 내지 않는다(위 머리말).
 */
export function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = randomBytes(16)
  if (bytes === null) {
    throw new Error('uuid: 이 환경에 crypto.randomUUID 도 crypto.getRandomValues 도 없습니다')
  }

  /*
   * RFC 4122 §4.4 — 버전 니블은 `4`, variant 니블은 `8·9·a·b` 중 하나여야 한다.
   * 이 두 줄을 빼면 «모양만» UUID 인 난수가 되고, 서버가 UUID 로 검증하면 422 로 떨어진다.
   */
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80

  const h = hex(bytes)
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}
